import React, { useState, useMemo, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Checkbox, Button, TextField, InputAdornment, CircularProgress,
  Typography, IconButton, MenuItem, Select, FormControl, InputLabel,
  Chip, useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FilterListIcon from "@mui/icons-material/FilterList";
import EventIcon from "@mui/icons-material/Event";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { fetchJobHours, fetchIwoGroupByAdditional } from "../../action/ProductionAction";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import homeImage from "../../assets/images/BT.png";

const BRAND = "#330066";

const baseHeadCell = {
  background: BRAND, color: "#fff", fontSize: 11, fontWeight: 600,
  py: "6px", px: "6px", borderBottom: "none",
  position: "sticky", top: 0, zIndex: 10,
};

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 10,
  overflow: "hidden",
});

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatDate   = (d) => new Date(d).toISOString().split("T")[0];
const formatTime   = (d) => new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
const getTodayDate = ()  => new Date().toISOString().split("T")[0];

const formatDecimalNo = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = parseFloat(v);
  return isNaN(n) ? v : n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, gradient }) {
  return (
    <Box sx={{
      flex: 1, borderRadius: "10px", p: "8px 10px",
      background: gradient, color: "#fff",
      boxShadow: "0 2px 6px rgba(0,0,0,0.10)", minWidth: 0,
    }}>
      <Typography sx={{ fontSize: 9, fontWeight: 500, opacity: 0.85, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>
        {value}
        {sub && <Box component="span" sx={{ fontSize: 11, fontWeight: 600, ml: 0.4, opacity: 0.85 }}>{sub}</Box>}
      </Typography>
    </Box>
  );
}

// ── Results view ──────────────────────────────────────────────────────────────
function ResultsView({
  JobHoursData, jobHoursLoading, totalEstimatedHours, totalActualHours,
  totalDifference, actualPercentage, balancePercentage, unitTurnTargetDate,
  additionalName, handleBackToSelection, handleExportPDF,
}) {
  const isMobile  = useMediaQuery("(max-width:600px)");
  const balanceNum = parseFloat(totalDifference);

  return (
    <Box sx={{ p: { xs: "6px", sm: "8px" } }}>

      {/* Header */}
      <Box sx={{
        background: BRAND, borderRadius: "10px", p: "8px 12px",
        mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <AssessmentIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 9, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Man Hours Report · {getTodayDate()}
            </Typography>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: { xs: 13, sm: 15 }, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {additionalName || `${JobHoursData?.length || 0} IWO(s) Selected`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleBackToSelection} size="small"
          sx={{ color: "#fff", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "7px", flexShrink: 0, "&:hover": { background: "rgba(255,255,255,0.22)" } }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {jobHoursLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
          <CircularProgress size={38} thickness={4} sx={{ color: BRAND }} />
          <Typography sx={{ mt: 2, color: "#64748b", fontSize: 13 }}>Calculating man hours…</Typography>
        </Box>
      ) : (
        <>
          {/* Summary cards */}
          <Box sx={{ display: "flex", gap: 0.8, mb: 1 }}>
            <StatCard label="Est. Hours"   value={formatDecimalNo(totalEstimatedHours)}
              gradient="linear-gradient(135deg,#330066 0%,#1a6099 100%)" />
            <StatCard label="Actual Hours" value={formatDecimalNo(totalActualHours)}
              sub={`(${actualPercentage}%)`}
              gradient="linear-gradient(135deg,#16a34a 0%,#22c55e 100%)" />
            <StatCard label="Balance"      value={formatDecimalNo(totalDifference)}
              sub={`(${balancePercentage}%)`}
              gradient={balanceNum >= 0
                ? "linear-gradient(135deg,#d97706 0%,#f59e0b 100%)"
                : "linear-gradient(135deg,#b91c1c 0%,#dc2626 100%)"} />
          </Box>

          {/* IWO Details table */}
          <Card sx={{ mb: 1 }}>
            <Box sx={{ px: "10px", py: "7px", borderBottom: "0.5px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>IWO Details</Typography>
              <Typography sx={{ fontSize: 10, color: "#64748b" }}>{JobHoursData?.length || 0} records</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 280, overflowX: "auto" }}>
              <Table size="small" stickyHeader sx={{ minWidth: isMobile ? 580 : 700 }}>
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Description",    align: "left",   w: "22%" },
                      { label: "Target Date",    align: "center", w: "10%" },
                      { label: "Remaining Days", align: "center", w: "11%" },
                      { label: "Phy. %",         align: "center", w: "8%"  },
                      { label: "Total Days",     align: "center", w: "8%"  },
                      { label: "Completed",      align: "center", w: "8%"  },
                      { label: "Supervisor",     align: "left",   w: "13%" },
                      { label: "IWO No",         align: "center", w: "8%"  },
                      { label: "Start",          align: "center", w: "8%"  },
                      { label: "End",            align: "center", w: "8%"  },
                    ].map((h) => (
                      <TableCell key={h.label} sx={{ ...baseHeadCell, width: h.w, textAlign: h.align }}>{h.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {JobHoursData?.map((item, i) => {
                    const totalDays     = parseFloat(item.TotalDays)     || 0;
                    const completedDays = parseFloat(item.CompletedDays) || 0;
                    const remainingDays = parseFloat(item.RemainingDays) || 0;
                    const completedPct  = totalDays > 0 ? ((completedDays / totalDays) * 100).toFixed(1) : "0.0";
                    const remainingPct  = totalDays > 0 ? ((remainingDays / totalDays) * 100).toFixed(1) : "0.0";
                    const isNeg         = remainingDays < 0;
                    const updatedDate   = item.UpdatedDate ? new Date(item.UpdatedDate).toISOString().split("T")[0] : "-";
                    return (
                      <TableRow key={i} sx={{ "&:nth-of-type(even) td": { background: "#f8faff" }, "&:last-child td": { borderBottom: "none" } }}>
                        <TableCell sx={{ fontSize: 11, px: "6px", py: "5px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>{item.Description}</TableCell>
                        <TableCell sx={{ fontSize: 10, textAlign: "center", px: "5px", py: "5px", whiteSpace: "nowrap" }}>{item.JobEndDate}</TableCell>
                        <TableCell sx={{ textAlign: "center", px: "5px", py: "5px" }}>
                          <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", background: isNeg ? "rgba(220,38,38,0.08)" : "rgba(22,163,74,0.08)", borderRadius: "6px", px: 0.8, py: 0.3, minWidth: 38 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: isNeg ? "#dc2626" : "#16a34a", lineHeight: 1.1 }}>{formatDecimalNo(item.RemainingDays)}</Typography>
                            <Typography sx={{ fontSize: 9, fontWeight: 600, color: isNeg ? "#dc2626" : "#16a34a", opacity: 0.85 }}>{remainingPct}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", px: "5px", py: "5px" }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#1e293b" }}>{formatDecimalNo(item.PhysicalCompletion) !== "-" ? `${formatDecimalNo(item.PhysicalCompletion)}%` : "-"}</Typography>
                          <Typography sx={{ fontSize: 9, color: "#94a3b8" }}>{updatedDate}</Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", fontSize: 11, fontWeight: 600, px: "5px", py: "5px" }}>{formatDecimalNo(item.TotalDays)}</TableCell>
                        <TableCell sx={{ textAlign: "center", px: "5px", py: "5px" }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#1e293b" }}>{formatDecimalNo(item.CompletedDays)}</Typography>
                          <Typography sx={{ fontSize: 9, color: "#94a3b8" }}>{completedPct}%</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, px: "6px", py: "5px", whiteSpace: "normal", wordBreak: "break-word" }}>{item.Supervisor}</TableCell>
                        <TableCell sx={{ textAlign: "center", px: "5px", py: "5px" }}>
                          <Box component="span" sx={{ display: "inline-block", px: "5px", py: "1px", borderRadius: "6px", fontSize: 10, fontWeight: 700, background: "#e8f0fe", color: BRAND }}>{item.IwoNo}</Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 10, textAlign: "center", px: "5px", py: "5px", whiteSpace: "nowrap" }}>{item.JobStartDate}</TableCell>
                        <TableCell sx={{ fontSize: 10, textAlign: "center", px: "5px", py: "5px", whiteSpace: "nowrap" }}>{item.JobEndDate}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Hull Turning Date banner */}
          {unitTurnTargetDate && (
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8,
              mb: 1, py: "7px", px: 1.5,
              background: "linear-gradient(90deg,rgba(2,132,199,0.10) 0%,rgba(21,76,121,0.13) 50%,rgba(2,132,199,0.10) 100%)",
              borderRadius: "10px", border: "1px dashed rgba(21,76,121,0.35)", flexWrap: "wrap",
            }}>
              <EventIcon sx={{ fontSize: 14, color: BRAND, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: BRAND, textTransform: "uppercase", letterSpacing: "0.5px" }}>Hull Turning Date:</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0369a1" }}>
                {new Date(unitTurnTargetDate).toISOString().split("T")[0]}
              </Typography>
            </Box>
          )}

          {/* Hours Breakdown table */}
          <Card sx={{ mb: 1 }}>
            <Box sx={{ px: "10px", py: "7px", borderBottom: "0.5px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>Hours Breakdown</Typography>
              <Typography sx={{ fontSize: 10, color: "#64748b" }}>{JobHoursData?.length || 0} records</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 240, overflowX: "auto" }}>
              <Table size="small" stickyHeader sx={{ minWidth: 300 }}>
                <TableHead>
                  <TableRow>
                    {["IWO No", "Estimated", "Actual", "Balance"].map((h) => (
                      <TableCell key={h} sx={{ ...baseHeadCell, textAlign: "center" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {JobHoursData?.map((item, i) => {
                    const est      = parseFloat(item.EstimatedHours) || 0;
                    const act      = parseFloat(item.ActualHours)    || 0;
                    const bal      = est - act;
                    const isNegBal = bal < 0;
                    return (
                      <TableRow key={i} sx={{ "&:nth-of-type(even) td": { background: "#f8faff" }, "&:last-child td": { borderBottom: "none" } }}>
                        <TableCell sx={{ textAlign: "center", px: "6px", py: "5px" }}>
                          <Box component="span" sx={{ display: "inline-block", px: "6px", py: "1px", borderRadius: "6px", fontSize: 10, fontWeight: 700, background: "#e8f0fe", color: BRAND }}>{item.IwoNo}</Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#1e293b", px: "6px", py: "5px" }}>{formatDecimalNo(est)}</TableCell>
                        <TableCell sx={{ textAlign: "center", px: "6px", py: "5px" }}>
                          <Box component="span" sx={{ display: "inline-block", px: "6px", py: "1px", borderRadius: "6px", fontSize: 11, fontWeight: 600, background: "#dcfce7", color: "#16a34a" }}>{formatDecimalNo(act)}</Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", px: "6px", py: "5px" }}>
                          <Box component="span" sx={{ display: "inline-block", px: "6px", py: "1px", borderRadius: "6px", fontSize: 11, fontWeight: 700, background: isNegBal ? "rgba(220,38,38,0.08)" : "rgba(245,158,11,0.1)", color: isNegBal ? "#dc2626" : "#d97706" }}>{formatDecimalNo(bal)}</Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Action buttons */}
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 0.8 }}>
            <Button onClick={handleExportPDF} variant="contained" fullWidth startIcon={<DownloadIcon />}
              sx={{ background: "#16a34a", color: "#fff", borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12, py: 0.8, boxShadow: "0 2px 8px rgba(22,163,74,0.25)", "&:hover": { background: "#15803d" } }}>
              Export PDF
            </Button>
            <Button onClick={handleBackToSelection} variant="outlined" fullWidth startIcon={<ArrowBackIcon />}
              sx={{ borderColor: BRAND, color: BRAND, borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12, py: 0.8, borderWidth: "1.5px", "&:hover": { background: "rgba(21,76,121,0.05)", borderColor: "#0e3c66" } }}>
              Back to Production
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AccumulatedTable({ rows = [] }) {
  const dispatch  = useDispatch();
  const isMobile  = useMediaQuery("(max-width:600px)");

  const { JobHoursData, jobHoursLoading, IwoGroupData, iwoGroupLoading } = useSelector(
    (s) => s.productionAccumulate
  );

  const [search,             setSearch]             = useState("");
  const [selectedIwo,        setSelectedIwo]        = useState([]);
  const [showResults,        setShowResults]        = useState(false);
  const [selectedJCat,       setSelectedJCat]       = useState("");
  const [selectedJMain,      setSelectedJMain]      = useState("");
  const [selectedAdditional, setSelectedAdditional] = useState("");

  useEffect(() => { dispatch(fetchIwoGroupByAdditional()); }, [dispatch]);

  const { totalEstimatedHours, totalActualHours, totalDifference, actualPercentage, balancePercentage } =
    useMemo(() => {
      if (!JobHoursData?.length)
        return { totalEstimatedHours: 0, totalActualHours: 0, totalDifference: 0, actualPercentage: 0, balancePercentage: 0 };
      const est  = JobHoursData.reduce((s, i) => s + (parseFloat(i.EstimatedHours) || 0), 0);
      const act  = JobHoursData.reduce((s, i) => s + (parseFloat(i.ActualHours)    || 0), 0);
      const diff = est - act;
      return {
        totalEstimatedHours: est.toFixed(2),
        totalActualHours:    act.toFixed(2),
        totalDifference:     diff.toFixed(2),
        actualPercentage:    (est > 0 ? (act  / est) * 100 : 0).toFixed(0),
        balancePercentage:   (est > 0 ? (diff / est) * 100 : 0).toFixed(0),
      };
    }, [JobHoursData]);

  const unitTurnTargetDate = useMemo(() => JobHoursData?.[0]?.UnitTurnTargetDate || null, [JobHoursData]);

  const uniqueRows = useMemo(() => {
    const map = new Map();
    (Array.isArray(rows) ? rows : Object.values(rows)).forEach((r) => { if (!map.has(r.iwo_no)) map.set(r.iwo_no, r); });
    return Array.from(map.values());
  }, [rows]);

  const uniqueJCats  = useMemo(() => [...new Set(uniqueRows.map((r) => r.JCat).filter(Boolean))].sort(), [uniqueRows]);
  const uniqueJMains = useMemo(() => {
    if (!selectedJCat) return [];
    return [...new Set(uniqueRows.filter((r) => r.JCat === selectedJCat).map((r) => r.JMain).filter(Boolean))].sort();
  }, [uniqueRows, selectedJCat]);

  const filteredRows = useMemo(() => {
    let f = uniqueRows;
    if (selectedJCat)  f = f.filter((r) => r.JCat  === selectedJCat);
    if (selectedJMain) f = f.filter((r) => r.JMain === selectedJMain);
    if (search.trim()) {
      const kw = search.trim().toLowerCase();
      f = f.filter((r) =>
        String(r.iwo_no    ?? "").toLowerCase().includes(kw) ||
        String(r.spec      ?? "").toLowerCase().includes(kw) ||
        String(r.Supervisor ?? "").toLowerCase().includes(kw)
      );
    }
    return f;
  }, [uniqueRows, search, selectedJCat, selectedJMain]);

  const handleCheckboxChange = (iwoNo) =>
    setSelectedIwo((prev) => prev.includes(iwoNo) ? prev.filter((i) => i !== iwoNo) : [...prev, iwoNo]);

  const handleCalculate = async () => {
    if (!selectedIwo.length) { alert("Please select at least one IWO"); return; }
    await dispatch(fetchJobHours(selectedIwo));
    setShowResults(true);
  };

  const handleAdditionalChange = async (e) => {
    const name = e.target.value;
    setSelectedAdditional(name);
    if (name) {
      const group = IwoGroupData.find((g) => g.Additional === name);
      if (group?.IwoList?.length > 0) {
        const iwoNos = group.IwoList.map((i) => (typeof i === "object" ? i.IwoNo : i));
        setSelectedIwo(iwoNos);
        await dispatch(fetchJobHours(iwoNos));
        setShowResults(true);
      }
    } else {
      setSelectedIwo([]);
      setShowResults(false);
    }
  };

  const handleBackToSelection = () => {
    setShowResults(false); setSelectedAdditional(""); setSelectedIwo([]);
    setSelectedJCat(""); setSelectedJMain(""); setSearch("");
  };
  const handleJCatChange    = (e) => { setSelectedJCat(e.target.value); setSelectedJMain(""); };
  const handleJMainChange   = (e) => setSelectedJMain(e.target.value);
  const handleClearFilters  = () => { setSelectedJCat(""); setSelectedJMain(""); setSearch(""); setSelectedAdditional(""); };

  // ── PDF Export (unchanged logic) ──────────────────────────────────────────
  const handleExportPDF = () => {
    if (!JobHoursData?.length) { alert("No data to export"); return; }
    const doc  = new jsPDF("l", "mm", "a4");
    const at   = new Date();
    const pw   = doc.internal.pageSize.getWidth();
    const ph   = doc.internal.pageSize.getHeight();
    const aName = JobHoursData?.[0]?.Additional || selectedAdditional || "";

    doc.addImage(homeImage, "PNG", 0, 0, 297, 44);
    doc.setFontSize(20).setFont("helvetica", "normal").setTextColor(21, 76, 121);
    doc.text("Man Hours Calculation Report", pw / 2, 53, { align: "center" });
    if (aName) { doc.setFontSize(14).setFont("helvetica", "bold"); doc.text(aName, pw / 2, 61, { align: "center" }); }
    const divY = aName ? 65 : 59;
    doc.setDrawColor(21, 76, 121).setLineWidth(0.5).line(15, divY, pw - 15, divY);

    const sx = 15, colW = (pw - 30) / 4, lY = divY + 8, vY = lY + 7;
    doc.setFontSize(10).setFont("helvetica", "normal").setTextColor(120, 120, 120);
    ["Date", "Total Est. Hours", "Total Actual Hours", "Total Balance"].forEach((l, i) => doc.text(l, sx + colW * i, lY));
    doc.setFontSize(13).setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121); doc.text(formatDate(at), sx, vY);
    doc.text(`${formatDecimalNo(totalEstimatedHours)} hrs`, sx + colW, vY);
    doc.setTextColor(22, 163, 74); doc.text(`${formatDecimalNo(totalActualHours)} hrs (${actualPercentage}%)`, sx + colW * 2, vY);
    const bn = parseFloat(totalDifference);
    doc.setTextColor(bn >= 0 ? 202 : 220, bn >= 0 ? 138 : 38, bn >= 0 ? 4 : 38);
    doc.text(`${formatDecimalNo(totalDifference)} hrs (${balancePercentage}%)`, sx + colW * 3, vY);
    doc.setDrawColor(210, 215, 220).setLineWidth(0.3).line(15, vY + 5, pw - 15, vY + 5);

    const detailCols = ["IWO No","Description","Total\nDays","Completed\nDays","Remaining\nDays","Phy.\nCompletion","Updated\nDate","Supervisor","Start\nDate","End\nDate"];
    const detailRows = JobHoursData.map((item) => {
      const td = parseFloat(item.TotalDays) || 0, cd = parseFloat(item.CompletedDays) || 0, rd = parseFloat(item.RemainingDays) || 0;
      return [item.IwoNo||"",item.Description||"",formatDecimalNo(item.TotalDays),
        `${formatDecimalNo(cd)}\n(${td>0?((cd/td)*100).toFixed(1):"0.0"}%)`,
        `${formatDecimalNo(rd)}\n(${td>0?((rd/td)*100).toFixed(1):"0.0"}%)`,
        formatDecimalNo(item.PhysicalCompletion)!=="-"?`${formatDecimalNo(item.PhysicalCompletion)}%`:"-",
        item.UpdatedDate?new Date(item.UpdatedDate).toISOString().split("T")[0]:"-",
        item.Supervisor||"",item.JobStartDate||"",item.JobEndDate||""];
    });
    autoTable(doc, {
      head:[detailCols],body:detailRows,startY:vY+9,margin:{left:15,right:15},
      styles:{fontSize:8,cellPadding:{top:4,bottom:4,left:3,right:3},valign:"middle",overflow:"linebreak",lineColor:[226,232,240],lineWidth:0.2},
      headStyles:{fillColor:[21,76,121],textColor:255,fontStyle:"bold",halign:"center",valign:"middle",fontSize:8},
      columnStyles:{0:{cellWidth:18,halign:"center",fontStyle:"bold"},1:{cellWidth:62},2:{cellWidth:18,halign:"center"},3:{cellWidth:26,halign:"center"},4:{cellWidth:26,halign:"center"},5:{cellWidth:22,halign:"center"},6:{cellWidth:24,halign:"center"},7:{cellWidth:38},8:{cellWidth:22,halign:"center"},9:{cellWidth:22,halign:"center"}},
      alternateRowStyles:{fillColor:[245,248,252]},
      didParseCell:(d)=>{ if(d.section==="body"&&d.column.index===4){const raw=parseFloat(JobHoursData[d.row.index]?.RemainingDays);d.cell.styles.textColor=raw>=0?[22,163,74]:[220,38,38];d.cell.styles.fontStyle="bold";}},
      didDrawPage:(d)=>{ doc.setFontSize(8).setTextColor(160);doc.text(`Generated on ${formatDate(at)} ${formatTime(at)}`,pw/2,ph-8,{align:"center"});doc.text(`Page ${d.pageNumber}`,pw-15,ph-8,{align:"right"});if(aName)doc.text(`Group: ${aName}`,15,ph-8);},
    });

    let curY = doc.lastAutoTable.finalY + 8;
    if (unitTurnTargetDate) {
      const hullStr = new Date(unitTurnTargetDate).toISOString().split("T")[0];
      doc.setFillColor(232,244,253).setDrawColor(21,76,121).setLineWidth(0.3);
      doc.roundedRect(15,curY,pw-30,9,2,2,"FD");
      const tY=curY+6;
      doc.setFontSize(9).setFont("helvetica","normal").setTextColor(80,80,80);
      const lbl="Hull Turning Date:"; doc.text(lbl,20,tY);
      doc.setFont("helvetica","bold").setTextColor(3,105,161);
      doc.text(hullStr,20+doc.getTextWidth(lbl)+3,tY);
      curY+=16;
    }
    doc.setFontSize(11).setFont("helvetica","bold").setTextColor(21,76,121);
    doc.text("Hours Breakdown",15,curY);
    doc.setDrawColor(21,76,121).setLineWidth(0.4).line(15,curY+2,pw-15,curY+2);
    const hoursRows=JobHoursData.map((item)=>{const e=parseFloat(item.EstimatedHours)||0,a=parseFloat(item.ActualHours)||0;return[item.IwoNo||"",formatDecimalNo(e),formatDecimalNo(a),formatDecimalNo(e-a)];});
    autoTable(doc,{
      head:[["IWO No","Estimated Hours","Actual Hours","Balance"]],body:hoursRows,startY:curY+6,margin:{left:15,right:15},
      styles:{fontSize:8,cellPadding:{top:4,bottom:4,left:4,right:4},valign:"middle",halign:"center",lineColor:[226,232,240],lineWidth:0.2},
      headStyles:{fillColor:[21,76,121],textColor:255,fontStyle:"bold",halign:"center",fontSize:8},
      columnStyles:{0:{cellWidth:30,fontStyle:"bold",textColor:[21,76,121]},1:{cellWidth:40},2:{cellWidth:40},3:{cellWidth:40}},
      alternateRowStyles:{fillColor:[245,248,252]},
      didParseCell:(d)=>{ if(d.section!=="body"||d.column.index!==3)return;const item=JobHoursData[d.row.index];if(item){const b=(parseFloat(item.EstimatedHours)||0)-(parseFloat(item.ActualHours)||0);d.cell.styles.textColor=b>=0?[202,138,4]:[220,38,38];d.cell.styles.fontStyle="bold";}},
      didDrawPage:(d)=>{ doc.setFontSize(8).setTextColor(160);doc.text(`Generated on ${formatDate(at)} ${formatTime(at)}`,pw/2,ph-8,{align:"center"});doc.text(`Page ${d.pageNumber}`,pw-15,ph-8,{align:"right"});if(aName)doc.text(`Group: ${aName}`,15,ph-8);},
    });
    doc.save(`Man_Hours_Report_${formatDate(at)}.pdf`);
  };

  // ── Results view ──
  if (showResults) {
    return (
      <ResultsView
        JobHoursData={JobHoursData}
        jobHoursLoading={jobHoursLoading}
        totalEstimatedHours={totalEstimatedHours}
        totalActualHours={totalActualHours}
        totalDifference={totalDifference}
        actualPercentage={actualPercentage}
        balancePercentage={balancePercentage}
        unitTurnTargetDate={unitTurnTargetDate}
        additionalName={JobHoursData?.[0]?.Additional || selectedAdditional || ""}
        selectedIwo={selectedIwo}
        handleBackToSelection={handleBackToSelection}
        handleExportPDF={handleExportPDF}
        formatDecimalNo={formatDecimalNo}
      />
    );
  }

  // ── IWO Selection view ────────────────────────────────────────────────────
  // Shared compact Select sx
  const selectSx = (borderW = "1.5px") => ({
    borderRadius: "8px", background: "#fff",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BRAND, borderWidth: borderW },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND, borderWidth: "2px" },
    "& .MuiSelect-select": { py: "9px", fontSize: 13 },
  });

  return (
    <Box sx={{ p: { xs: "4px", sm: "6px" } }}>

      {/* Quick Select */}
      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel sx={{ color: BRAND, fontSize: 13, "&.Mui-focused": { color: BRAND } }}>Quick Search</InputLabel>
        <Select value={selectedAdditional} onChange={handleAdditionalChange} label="Quick Search"
          disabled={iwoGroupLoading} sx={selectSx("2px")}>
          <MenuItem value=""><em>All (Manual Selection)</em></MenuItem>
          {IwoGroupData?.length > 0
            ? [...IwoGroupData]
                .sort((a, b) => (a.IwoList?.[0]?.StartDate ?? "").localeCompare(b.IwoList?.[0]?.StartDate ?? ""))
                .map((group) => (
                  <MenuItem key={group.Additional} value={group.Additional} sx={{ fontSize: 13 }}>
                    {group.Additional}
                    <Typography component="span" sx={{ ml: 0.75, color: "#94a3b8", fontSize: 11 }}>
                      ({group.IwoList?.length || 0} IWOs)
                      {group.IwoList?.every(i => i.PhysicalCompletion === "100")
                        ? <span style={{ color: "#16a34a", marginLeft: 4 }}>Completed</span>
                        : <span style={{ color: "#f59e0b", marginLeft: 4 }}>In Progress</span>}
                    </Typography>
                  </MenuItem>
                ))
            : <MenuItem disabled><em>No groups available</em></MenuItem>}
        </Select>
      </FormControl>

      {/* Secondary filters */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" }, gap: { xs: 0.8, sm: 1 }, mb: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: BRAND, fontSize: 13, "&.Mui-focused": { color: BRAND } }}>Job Category</InputLabel>
          <Select value={selectedJCat} onChange={handleJCatChange} label="Job Category" sx={selectSx()}>
            <MenuItem value=""><em>All Categories</em></MenuItem>
            {uniqueJCats.map((j) => <MenuItem key={j} value={j} sx={{ fontSize: 13 }}>{j}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={!selectedJCat}>
          <InputLabel sx={{ color: BRAND, fontSize: 13, "&.Mui-focused": { color: BRAND }, "&.Mui-disabled": { color: "rgba(21,76,121,0.35)" } }}>Job Main</InputLabel>
          <Select value={selectedJMain} onChange={handleJMainChange} label="Job Main" sx={selectSx()}>
            <MenuItem value=""><em>All Mains</em></MenuItem>
            {uniqueJMains.map((j) => <MenuItem key={j} value={j} sx={{ fontSize: 13 }}>{j}</MenuItem>)}
          </Select>
        </FormControl>

        <Button fullWidth variant="outlined" size="small" onClick={handleClearFilters} startIcon={<FilterListIcon />}
          sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, borderColor: BRAND, color: BRAND, fontWeight: 600, textTransform: "none", borderRadius: "8px", borderWidth: "1.5px", fontSize: 12, "&:hover": { borderColor: "#0e3c66", background: "rgba(21,76,121,0.04)" } }}>
          Clear Filters
        </Button>
      </Box>

      {/* Active filter chips */}
      {(selectedJCat || selectedJMain || selectedAdditional) && (
        <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap", mb: 1, alignItems: "center" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: BRAND }}>Active Filters:</Typography>
          {selectedAdditional && <Chip label={`Additional: ${selectedAdditional}`} size="small" onDelete={() => { setSelectedAdditional(""); setSelectedIwo([]); }} sx={{ background: BRAND, color: "#fff", fontSize: 11, "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />}
          {selectedJCat  && <Chip label={`JCat: ${selectedJCat}`}  size="small" onDelete={() => setSelectedJCat("")}  sx={{ background: "#0369a1", color: "#fff", fontSize: 11, "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />}
          {selectedJMain && <Chip label={`JMain: ${selectedJMain}`} size="small" onDelete={() => setSelectedJMain("")} sx={{ background: "#16a34a", color: "#fff", fontSize: 11, "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />}
        </Box>
      )}

      {/* Search */}
      <TextField
        fullWidth size="small"
        placeholder="Search IWO No, Description, or Supervisor..."
        value={search} onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", background: "#fff", fontSize: 13, "& fieldset": { borderColor: BRAND, borderWidth: "1.5px" }, "&:hover fieldset": { borderColor: BRAND }, "&.Mui-focused fieldset": { borderColor: BRAND, borderWidth: "2px" } } }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: BRAND, fontSize: 18 }} /></InputAdornment>,
          endAdornment: search && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch("")}><CloseIcon sx={{ fontSize: 14, color: "#6b7280" }} /></IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Selected IWO chips */}
      {selectedIwo.length > 0 && (
        <Box sx={{ mb: 1, display: "flex", gap: 0.6, flexWrap: "wrap", alignItems: "center" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: BRAND }}>Selected ({selectedIwo.length}):</Typography>
          {selectedIwo.map((iwo) => (
            <Chip key={iwo} label={iwo} size="small" onDelete={() => handleCheckboxChange(iwo)}
              sx={{ height: 22, background: BRAND, color: "#fff", fontSize: 10, fontWeight: 600, "& .MuiChip-label": { px: 0.8 }, "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", fontSize: 14 } }} />
          ))}
        </Box>
      )}

      {/* Count */}
      <Typography sx={{ fontSize: 11, color: "#6b7280", mb: 0.8 }}>
        Showing {filteredRows.length} of {uniqueRows.length} records{selectedIwo.length > 0 && ` • ${selectedIwo.length} selected`}
      </Typography>

      {/* Main table */}
      <Card>
        <TableContainer sx={{ maxHeight: isMobile ? "calc(100vh - 340px)" : "calc(100vh - 320px)", overflowX: "auto" }}>
          <Table size="small" stickyHeader sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...baseHeadCell, width: 36, px: "3px" }} />
                {[
                  { label: "IWO No",      w: "10%", align: "center" },
                  { label: "Description", w: "30%", align: "left"   },
                  { label: "Job Cat",     w: "9%",  align: "center" },
                  { label: "Job Main",    w: "9%",  align: "center" },
                  { label: "Supervisor",  w: "17%", align: "left"   },
                  { label: "Start Date",  w: "12%", align: "center" },
                  { label: "End Date",    w: "12%", align: "center" },
                ].map((h) => (
                  <TableCell key={h.label} sx={{ ...baseHeadCell, width: h.w, textAlign: h.align }}>{h.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length > 0 ? (
                filteredRows.map((row, i) => {
                  const isSelected = selectedIwo.includes(row.iwo_no);
                  return (
                    <TableRow key={i} onClick={() => handleCheckboxChange(row.iwo_no)}
                      sx={{
                        cursor: "pointer",
                        background: isSelected ? "rgba(21,76,121,0.06) !important" : undefined,
                        "&:nth-of-type(even) td": { background: "#f8faff" },
                        "&:hover td": { background: "rgba(21,76,121,0.07)" },
                        "&:last-child td": { borderBottom: "none" },
                      }}>
                      <TableCell padding="checkbox" sx={{ px: "3px", py: "4px" }}>
                        <Checkbox checked={isSelected} onChange={() => handleCheckboxChange(row.iwo_no)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: "#94a3b8", "&.Mui-checked": { color: BRAND }, p: "3px" }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", px: "6px", py: "5px" }}>
                        <Box component="span" sx={{ display: "inline-block", px: "6px", py: "1px", borderRadius: "6px", fontSize: 10, fontWeight: 700, background: "#e8f0fe", color: BRAND }}>{row.iwo_no || "-"}</Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, px: "6px", py: "5px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>{row.spec || "-"}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontSize: 11, px: "5px", py: "5px" }}>{row.JCat || "-"}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontSize: 11, px: "5px", py: "5px" }}>{row.JMain || "-"}</TableCell>
                      <TableCell sx={{ fontSize: 11, px: "6px", py: "5px", whiteSpace: "normal", wordBreak: "break-word" }}>{row.Supervisor || "-"}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontSize: 10, px: "5px", py: "5px", whiteSpace: "nowrap" }}>{row.sdate || "-"}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontSize: 10, px: "5px", py: "5px", whiteSpace: "nowrap" }}>{row.edate || "-"}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 5, fontSize: 12, color: "#64748b" }}>
                    No records found matching filters
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Calculate button */}
      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleCalculate} disabled={!selectedIwo.length}
          sx={{
            background: BRAND, color: "#fff", borderRadius: "8px",
            textTransform: "none", fontWeight: 600, fontSize: 13, px: 3, py: 0.8,
            boxShadow: "0 3px 10px rgba(21,76,121,0.2)",
            "&:hover": { background: "#0e3c66" },
            "&.Mui-disabled": { background: "rgba(21,76,121,0.3)", color: "#fff" },
          }}>
          Calculate Man Hours
        </Button>
      </Box>
    </Box>
  );
}